import { useCallback } from "react";
import { Badge, Box, Switch, Text } from "@salestrak/ui";
import { SetIntegrationProviderFeaturedDocument } from "#app/lib/data";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import {
  HeaderLabel,
  PanelCard,
  ScreenHeader,
  TableHeaderRow,
  TableRow,
} from "./admin-ui";
import { useAdminFetch } from "./admin-shared";
import type { DbProvider } from "./use-admin-data";

// Immediate-persist toggle mutation for a provider's `featured` flag — one
// request per flip (no save button), mirroring useFlagMutations.
function useIntegrationMutations(refetchProviders: () => void) {
  const { coreGql } = useAdminFetch();
  const setFeatured = useCallback(
    async (provider: DbProvider, featured: boolean) => {
      try {
        await coreGql(SetIntegrationProviderFeaturedDocument, {
          input: { provider: provider.slug, featured },
        });
        refetchProviders();
      } catch (err) {
        toastMutationError(err, "Failed to update integration");
      }
    },
    [coreGql, refetchProviders],
  );
  return { setFeatured };
}

function ProviderRow({
  provider,
  isLast,
  onToggle,
}: {
  provider: DbProvider;
  isLast: boolean;
  onToggle: (featured: boolean) => void;
}) {
  const count = provider.connectionCount;
  return (
    <TableRow last={isLast}>
      <Box fill>
        <Box align="center" direction="row" gap="xs">
          <Text size="sm" truncate={1} weight="semibold">
            {provider.displayName}
          </Text>
          <Badge variant="info">
            <Text mono size="xs">
              {provider.kind}
            </Text>
          </Badge>
        </Box>
        <Text mono size="2xs" truncate={1} variant="muted">
          {provider.slug}
        </Text>
      </Box>
      <Box align="start" width={140}>
        <Text size="sm" variant="muted">
          {count} {count === 1 ? "connection" : "connections"}
        </Text>
      </Box>
      <Box align="center" direction="row" gap="xs" width={120}>
        <Switch checked={provider.featured} onCheckedChange={onToggle} />
        <Text size="xs" variant={provider.featured ? "success" : "muted"} weight="medium">
          {provider.featured ? "Featured" : "Off"}
        </Text>
      </Box>
    </TableRow>
  );
}

export function IntegrationsScreen({
  providers,
  loading,
  refetchProviders,
}: {
  providers: DbProvider[];
  loading: boolean;
  refetchProviders: () => void;
}) {
  const { setFeatured } = useIntegrationMutations(refetchProviders);
  const featuredCount = providers.filter((p) => p.featured).length;

  return (
    <Box fill minHeight={0}>
      <ScreenHeader
        meta={`${providers.length} providers · ${featuredCount} featured`}
        title="Integrations"
      />
      <Box fill overflowY="auto">
        <Box paddingBottom="xl" paddingTop="sm" paddingX="lg">
          <PanelCard>
            <TableHeaderRow>
              <HeaderLabel fill>Provider</HeaderLabel>
              <HeaderLabel width={140}>Connections</HeaderLabel>
              <HeaderLabel width={120}>Featured</HeaderLabel>
            </TableHeaderRow>
            {!loading && providers.length === 0 ? (
              <Box align="center" paddingX="md" paddingY="lg">
                <Text size="sm" variant="muted">
                  No integration providers found
                </Text>
              </Box>
            ) : null}
            {providers.map((provider, index) => (
              <ProviderRow
                key={`${provider.slug}-${index}`}
                isLast={index === providers.length - 1}
                provider={provider}
                onToggle={(featured) => setFeatured(provider, featured)}
              />
            ))}
          </PanelCard>
        </Box>
      </Box>
    </Box>
  );
}
